import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useClients } from '../../hooks/useClients';
import { useCompanies } from '../../hooks/useCompanies';
import { useLeads } from '../../hooks/useLeads';
import { usePeople } from '../../hooks/usePeople';
import { useProjects } from '../../hooks/useProjects';
import { useUsers } from '../../hooks/useUsers';
import {
  MEETING_TYPE_OPTIONS,
  meetingTypeLabel,
  type Meeting,
  type MeetingInput,
} from '../../lib/meetingLogApi';
import { useActiveCompanyId } from '../../store/useCompanyStore';
import { useAuthStore } from '../../store/useAuthStore';
import { DateField } from '../ui/DateField';
import { TimeField } from '../ui/TimeField';
import { MultiSelect } from '../ui/MultiSelect';
import { Select } from '../ui/Select';
import {
  AttendeesEditor,
  draftFromContact,
  draftToInput,
  type AttendeeDraft,
} from './AttendeesEditor';

// Create/edit modal for a meeting (Întâlniri). Dates are entered as local
// wall-clock (date + time) and sent as ISO instants.

interface Props {
  initial?: Meeting;
  /** Create only: what the form starts with when opened from a lead/client/project page. */
  defaults?: { leadId?: string; clientId?: string; projectId?: string };
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (input: MeetingInput) => void | Promise<void>;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function localDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localTime(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function todayLocal(): string {
  return localDate(new Date().toISOString());
}

const inputCls =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
const labelCls = 'mb-1 block text-xs font-medium text-gray-600';

export function MeetingFormModal({ initial, defaults, submitting, onClose, onSubmit }: Props) {
  const { t } = useI18n();
  const currentUserId = useAuthStore((s) => s.user?.id) ?? '';
  const isCreate = !initial;

  const [title, setTitle] = useState(initial?.title ?? '');
  const [date, setDate] = useState(initial ? localDate(initial.startAt) : todayLocal());
  const [time, setTime] = useState(initial ? localTime(initial.startAt) : '10:00');
  const [endTime, setEndTime] = useState(initial?.endAt ? localTime(initial.endAt) : '');
  const [meetingType, setMeetingType] = useState<string>(initial?.meetingType ?? 'GENERAL');
  const [location, setLocation] = useState(initial?.location ?? '');
  const [ownerId, setOwnerId] = useState(initial?.owner.id ?? currentUserId);
  const [teamMemberIds, setTeamMemberIds] = useState<string[]>(
    initial?.teamMembers.map((m) => m.id) ?? [],
  );
  const [clientId, setClientId] = useState(initial?.client?.id ?? defaults?.clientId ?? '');
  const [projectId, setProjectId] = useState(initial?.project?.id ?? defaults?.projectId ?? '');
  const [leadId, setLeadId] = useState(initial?.lead?.id ?? defaults?.leadId ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [transcript, setTranscript] = useState('');
  const [attendees, setAttendees] = useState<AttendeeDraft[]>(
    initial?.attendees.map(draftFromContact) ?? [],
  );
  const [error, setError] = useState<string | null>(null);

  // Multi-company: firm tag ('' = default firm), hidden on single-firm installs.
  const { data: companies } = useCompanies();
  const multiFirm = (companies?.length ?? 0) > 1;
  const activeCompanyId = useActiveCompanyId();
  const [companyId, setCompanyId] = useState<string>(
    initial ? (initial.companyId ?? '') : (activeCompanyId ?? ''),
  );

  const { data: users } = useUsers();
  const { data: people } = usePeople({ active: 'true' });
  const { data: clientsData } = useClients({ sort: 'name', limit: 100, active: 'true' });
  const { data: projectsData } = useProjects({ sort: 'name', limit: 100 });
  // Open leads, most recently touched first; the one already on the meeting
  // stays selectable even if it was archived since.
  const { data: leadsData } = useLeads({ sort: 'updatedAt', order: 'desc', pageSize: 200 });
  const clients = (clientsData?.data ?? []).map((c) => ({ id: c.id, name: c.name }));
  const projects = projectsData?.projects ?? [];
  const leadOptions = (leadsData?.leads ?? []).map((l) => ({
    value: l.id,
    label: `${l.identifier} — ${l.title}${l.companyName ? ` (${l.companyName})` : ''}`,
  }));
  if (initial?.lead && !leadOptions.some((o) => o.value === initial.lead?.id)) {
    leadOptions.unshift({
      value: initial.lead.id,
      label: `${initial.lead.identifier} — ${initial.lead.title}`,
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError(t('meetings.form.titleRequired'));
      return;
    }
    if (!date || !time) {
      setError(t('meetings.form.dateRequired'));
      return;
    }
    const startAt = new Date(`${date}T${time}:00`);
    if (Number.isNaN(startAt.getTime())) {
      setError(t('meetings.form.dateRequired'));
      return;
    }
    let endAt: string | null = null;
    if (endTime) {
      const end = new Date(`${date}T${endTime}:00`);
      if (end < startAt) end.setDate(end.getDate() + 1);
      endAt = end.toISOString();
    }
    const input: MeetingInput = {
      title: title.trim(),
      startAt: startAt.toISOString(),
      endAt,
      location: location.trim() || null,
      meetingType,
      ownerId: ownerId || undefined,
      clientId: clientId || null,
      projectId: projectId || null,
      leadId: leadId || null,
      notes: notes.trim() || null,
      attendees: attendees.map(draftToInput),
      teamMemberIds,
    };
    if (multiFirm) input.companyId = companyId === '' ? null : companyId;
    if (isCreate && transcript.trim()) input.transcript = transcript.trim();
    try {
      await onSubmit(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {isCreate ? t('meetings.form.createTitle') : t('meetings.form.editTitle')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            <div>
              <label className={labelCls}>{t('meetings.form.title')}</label>
              <input
                className={inputCls}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <label className={labelCls}>{t('meetings.form.date')}</label>
                <DateField value={date} onChange={setDate} required />
              </div>
              <div>
                <label className={labelCls}>{t('meetings.form.time')}</label>
                <TimeField value={time} onChange={setTime} required />
              </div>
              <div>
                <label className={labelCls}>{t('meetings.form.endTime')}</label>
                <TimeField value={endTime} onChange={setEndTime} />
              </div>
              <div>
                <Select
                  label={t('meetings.form.type')}
                  value={meetingType}
                  onChange={setMeetingType}
                  options={MEETING_TYPE_OPTIONS.map((v) => ({
                    value: v,
                    label: meetingTypeLabel(v),
                  }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Select
                  label={t('meetings.form.owner')}
                  searchable
                  value={ownerId}
                  onChange={setOwnerId}
                  options={(users ?? []).map((u) => ({ value: u.id, label: u.name }))}
                />
              </div>
              <div>
                <MultiSelect
                  label={t('meetings.form.team')}
                  options={(people ?? []).map((p) => ({ value: p.id, label: p.fullName }))}
                  value={teamMemberIds}
                  onChange={setTeamMemberIds}
                  allLabel={t('meetings.form.teamNone')}
                  summaryLabel={(count) => t('meetings.form.teamSummary', { count })}
                />
              </div>
              <div>
                <Select
                  label={t('meetings.form.client')}
                  searchable
                  clearable
                  value={clientId}
                  onChange={setClientId}
                  placeholder={t('meetings.form.noClient')}
                  options={clients.map((c) => ({ value: c.id, label: c.name }))}
                />
              </div>
              <div>
                <Select
                  label={t('meetings.form.project')}
                  searchable
                  clearable
                  value={projectId}
                  onChange={setProjectId}
                  placeholder={t('meetings.form.noProject')}
                  options={projects.map((p) => ({
                    value: p.id,
                    label: p.code ? `${p.code} — ${p.name}` : p.name,
                  }))}
                />
              </div>
              <div className="sm:col-span-2">
                <Select
                  label={t('meetings.form.lead')}
                  searchable
                  clearable
                  value={leadId}
                  onChange={setLeadId}
                  placeholder={t('meetings.form.noLead')}
                  options={leadOptions}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>{t('meetings.form.location')}</label>
                <input
                  className={inputCls}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t('meetings.form.locationPlaceholder')}
                />
              </div>
              {multiFirm && (
                <div className="sm:col-span-2">
                  <Select
                    label={t('common.company')}
                    clearable
                    value={companyId}
                    onChange={setCompanyId}
                    placeholder={t('common.defaultCompany')}
                    options={(companies ?? []).map((c) => ({
                      value: c.id,
                      label: c.displayName ?? c.legalName,
                    }))}
                  />
                </div>
              )}
            </div>

            <div>
              <label className={labelCls}>{t('meetings.form.attendees')}</label>
              <AttendeesEditor
                value={attendees}
                onChange={setAttendees}
                clients={clients}
                defaultClientId={clientId || undefined}
              />
            </div>

            <div>
              <label className={labelCls}>{t('meetings.form.notes')}</label>
              <textarea
                className={inputCls}
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {isCreate && (
              <div>
                <label className={labelCls}>{t('meetings.form.transcript')}</label>
                <textarea
                  className={inputCls}
                  rows={6}
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  placeholder={t('meetings.form.transcriptPlaceholder')}
                />
                <p className="mt-1 text-xs text-gray-500">{t('meetings.form.transcriptHint')}</p>
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
